import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiLogOut,
  FiUser,
  FiKey,
  FiEdit3,
  FiBookOpen,
  FiCreditCard,
  FiChevronDown,
  FiMenu,
  FiUsers,
  FiShield,
  FiImage,
  FiCpu,
} from 'react-icons/fi';
import { useAuthStore, useUIStore } from '../../utils/store';
import { useLanguageStore, useT } from '../../utils/i18n';
import { ThemeToggle } from '../Common/ThemeToggle';

// Items shown only to editors (non-admins, non-managers)
const EDITOR_ITEMS = [
  { icon: FiUser, labelKey: 'header.profileAccount', path: '/settings/profile' },
];

// Items shown to admins (includes all settings + User Management)
const ADMIN_ITEMS = [
  { icon: FiUser,       labelKey: 'header.profileAccount',     path: '/settings/profile' },
  { icon: FiUsers,      labelKey: 'header.userManagement',     path: '/settings/user-management' },
  { icon: FiKey,        labelKey: 'header.aiProviderSettings', path: '/settings/ai-provider' },
  { icon: FiEdit3,      labelKey: 'header.promptEditor',       path: '/settings/prompt-editor' },
  { icon: FiBookOpen,   labelKey: 'header.editorialRules',     path: '/settings/editorial-rules' },
  { icon: FiCreditCard, labelKey: 'header.usageBilling',       path: '/settings/usage-billing' },
  { icon: FiShield,     labelKey: 'header.ipWhitelist',        path: '/settings/ip-whitelist' },
  { icon: FiImage,      labelKey: 'header.mediaAIProviders',   path: '/settings/media-ai' },
  { icon: FiCpu,        labelKey: 'header.automationSources',  path: '/settings/automation-sources' },
];

// Managers get User Management (scoped to non-admin accounts server-side —
// see AuthService.js), the cost report and pipeline source configuration.
const MANAGER_ITEMS = [
  { icon: FiUser,       labelKey: 'header.profileAccount',    path: '/settings/profile' },
  { icon: FiUsers,      labelKey: 'header.userManagement',    path: '/settings/user-management' },
  { icon: FiCreditCard, labelKey: 'header.usageBilling',      path: '/settings/usage-billing' },
  { icon: FiCpu,        labelKey: 'header.automationSources', path: '/settings/automation-sources' },
];

export const TopRibbon = () => {
  const { user, logout } = useAuthStore();
  const { lang, toggleLanguage } = useLanguageStore();
  const { toggleSidebar } = useUIStore();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const t = useT();
  const dropdownRef = useRef(null);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isManager = user?.role === 'manager';
  const dropdownItems = isAdmin ? ADMIN_ITEMS : isManager ? MANAGER_ITEMS : EDITOR_ITEMS;

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => {
    setOpen(false);
    logout();
    navigate('/login');
  };

  const initials = user?.name
    ? user.name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  return (
    <div className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 z-40 flex items-center justify-between px-3 md:px-6 gap-2">
      {/* Left — hamburger (mobile only) + brand */}
      <div className="flex items-center gap-2 md:gap-3 min-w-0">
        {/* Hamburger — opens/closes the mobile sidebar overlay drawer */}
        <button
          onClick={toggleSidebar}
          className="md:hidden p-2 -ml-1 rounded-lg text-gray-600 hover:bg-gray-100 transition flex-shrink-0"
          aria-label="Toggle sidebar menu"
        >
          <FiMenu size={20} />
        </button>

        <img
          src="/KultWorks Logo.png"
          alt="KultWorks"
          className="h-[1.7rem] object-contain kw-logo"
        />
        <span className="hidden lg:block text-xs font-medium text-gray-400 uppercase tracking-widest border-l border-gray-200 pl-3">
          Newsroom
        </span>
      </div>

      {/* Right */}
      <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
        {/* Language toggle — full pill on desktop, hidden on mobile (moved into profile dropdown) */}
        <button
          onClick={toggleLanguage}
          className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-primary-200 bg-primary-50 hover:bg-primary-100 transition text-sm font-semibold text-primary-700"
          title={lang === 'en' ? t('header.switchToTelugu') : t('header.switchToEnglish')}
        >
          <span className="text-xs font-bold">{t('lang.current')}</span>
          <span className="text-gray-400">|</span>
          <span className="text-xs text-primary-500">{t('lang.switch')}</span>
        </button>

        <ThemeToggle />

        {/* Token usage / AI provider badge pill — desktop (lg+) only; shown inside
            the profile dropdown on mobile/tablet instead (item 5 of FIX F). */}
        {user?.usage?.totalTokensUsed !== undefined && (
          <div className="hidden lg:flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-100 rounded-lg">
            <span className="text-xs text-gray-500">{t('header.apiUsage')}:</span>
            <span className="text-xs font-semibold text-gray-800">
              {(user.usage.totalTokensUsed / 1000).toFixed(1)}K {t('header.tokens')}
            </span>
          </div>
        )}

        {/* Profile avatar + dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-lg hover:bg-gray-100 transition"
            aria-haspopup="true"
            aria-expanded={open}
          >
            {/* Avatar */}
            <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white text-xs font-bold select-none">
              {initials}
            </div>
            {/* Name — hidden on small/tablet screens */}
            <span className="hidden lg:block text-sm font-medium text-gray-700 max-w-[120px] truncate">
              {user?.name}
            </span>
            <FiChevronDown
              size={14}
              className={`text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            />
          </button>

          {/* Dropdown menu */}
          {open && (
            <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-50 animate-slide-in">
              {/* User info header */}
              <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                {user?.role && (
                  <span className="inline-block mt-1 px-2 py-0.5 text-[10px] font-semibold rounded-full bg-primary-100 text-primary-700 uppercase tracking-wide">
                    {user.role}
                  </span>
                )}
              </div>

              {/* Mobile/tablet-only: language toggle + token usage badge,
                  hidden here on desktop since the ribbon already shows them. */}
              <div className="lg:hidden border-b border-gray-100 py-2 px-4 space-y-2">
                <button
                  onClick={toggleLanguage}
                  className="w-full flex items-center justify-between gap-1.5 px-3 py-1.5 rounded-lg border border-primary-200 bg-primary-50 hover:bg-primary-100 transition text-sm font-semibold text-primary-700"
                >
                  <span className="text-xs font-bold">{t('lang.current')}</span>
                  <span className="text-xs text-primary-500">{t('lang.switch')}</span>
                </button>
                {user?.usage?.totalTokensUsed !== undefined && (
                  <div className="flex items-center justify-between gap-2 px-3 py-1.5 bg-gray-50 border border-gray-100 rounded-lg">
                    <span className="text-xs text-gray-500">{t('header.apiUsage')}:</span>
                    <span className="text-xs font-semibold text-gray-800">
                      {(user.usage.totalTokensUsed / 1000).toFixed(1)}K {t('header.tokens')}
                    </span>
                  </div>
                )}
              </div>

              {/* Nav items */}
              <div className="py-1">
                {dropdownItems.map(({ icon: Icon, labelKey, path }) => (
                  <Link
                    key={path}
                    to={path}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                  >
                    <Icon size={15} className="text-gray-400 flex-shrink-0" />
                    <span>{t(labelKey)}</span>
                  </Link>
                ))}
              </div>

              {/* Logout */}
              <div className="border-t border-gray-100 py-1">
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-danger-600 hover:bg-danger-50 transition"
                >
                  <FiLogOut size={15} className="flex-shrink-0" />
                  <span>{t('header.logout')}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
